import {
  BackToTop,
  Button,
  ButtonContainer,
  ContentSection,
  Heading,
  IconList,
  InstructionStepper,
  Layout,
  Paragraph
} from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startLegitimation } from '../../redux/slices/appData/app-data.actions';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { getSession } from '../../redux/slices/appData/app-data.selectors';
import { trackingService } from '../../tracking/tracking.service';
import { HtmlContent } from '../../components/HtmlContent/HtmlContent';

const LegitimationLandingPage = (): JSX.Element => {
  const { t } = useTranslation();
  const session = useAppSelector(getSession);
  const [isOnfidoMotionEnabled, setIsOnfidoMotionEnabled] = useState(false);
  const brand = process.env.REACT_APP_BRAND_LABEL as string;
  const showEnableOnfidoBtn = process.env.REACT_APP_STAGE === 'dev' || process.env.REACT_APP_STAGE === 'int';

  const dispatch = useAppDispatch();
  const onLegitimationStartClick = () => {
    dispatch(
      startLegitimation({
        storefrontId: session?.id as string,
        token: session?.token as string,
        isOnfidoMotionEnabled
      })
    )
      .unwrap()
      .then(res => {
        window.open(res.redirectUrl, '_self');
      });
  };

  React.useEffect(() => {
    trackingService.trackPageView('Landing page', 'Landing page');
  }, []);

  return (
    <>
      <Heading level={3} className="u-text-center">
        {t('landingPage:heading:applicationApproved', { brand })}
      </Heading>
      <ContentSection>
        <Heading level={3} className="u-text-center u-mb-large">
          {t('landingPage:heading:applicationSteps')}
        </Heading>
        <Layout.Item s="1/3" className="u-block-center">
          <InstructionStepper displayIcons className="u-mb-large">
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:aboutYou') as string}
              icon="semantic-user"
              state="is-active"
            />
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:underwritingApproval') as string}
              icon="semantic-success"
            />
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:contract') as string}
              icon="semantic-brochure"
            />
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:takeAVideo') as string}
              icon="semantic-user"
            />
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:eSignature') as string}
              icon="semantic-premium-l"
            />
            <InstructionStepper.Step
              title={t('landingPage:content:stepper:vehicleDelivery') as string}
              icon="semantic-car-handover"
            />
          </InstructionStepper>
        </Layout.Item>
      </ContentSection>
      <ContentSection className="u-text-center">
        <Paragraph className="u-text-center u-mt">{t('landingPage:content:applicationApproved:line1')}</Paragraph>
        <Paragraph className="u-text-center">{t('landingPage:content:applicationApproved:line2')}</Paragraph>
      </ContentSection>
      <ContentSection className="u-text-center">
        <img src={`${process.env.PUBLIC_URL}/assets/landingPage/IdentificationSteps.jpg`} alt="Identification steps" />
      </ContentSection>
      <ContentSection>
        <Heading level={3} className="u-text-center">
          {t('landingPage:heading:whatYouWillNeed')}
        </Heading>
        <Layout.Item default="1/2" l="2/3" m="5/6" s="1/1" className="u-block-center">
          <IconList className="u-ml-xlarge@s-up">
            <IconList.Item icon="semantic-arrow-right">
              {t('landingPage:content:whatYouWillNeed:line1')}
              <HtmlContent as="div">{t('landingPage:content:whatYouWillNeed:line2')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">{t('landingPage:content:whatYouWillNeed:line3')}</IconList.Item>
          </IconList>
        </Layout.Item>
      </ContentSection>
      <ContentSection className="u-text-center">
        <Heading level={3}>{t('landingPage:heading:videoTips')}</Heading>
        <HtmlContent as={Paragraph} className="u-text center">
          {t('landingPage:content:videoTips:text1')}
        </HtmlContent>
        <HtmlContent as={Paragraph}>{t('landingPage:content:videoTips:text2')}</HtmlContent>
        <HtmlContent as={'h3'}>{t('landingPage:content:videoTips:text3')}</HtmlContent>
        <Layout center>
          <Layout.Item default="3/4">
            <img src={`${process.env.PUBLIC_URL}/assets/landingPage/OnfidoMove.png`} alt="onfido move tips" />
          </Layout.Item>
        </Layout>
      </ContentSection>
      <ContentSection>
        <Layout.Item default="1/2" l="2/3" m="5/6" s="1/1" className="u-block-center">
          <IconList className="u-ml-xlarge@s-up">
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip1')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip2')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip3')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip4')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip5')}</HtmlContent>
            </IconList.Item>
          </IconList>
        </Layout.Item>
        <ContentSection className="u-text-center">
          <HtmlContent as={'h3'} className="u-mt u-text-center">
            {t('landingPage:content:videoTips:text4')}
          </HtmlContent>
          <img src={`${process.env.PUBLIC_URL}/assets/landingPage/OnfidoWrongMove.png`} alt="onfido incorrect moves" />
        </ContentSection>
        <Layout.Item default="1/2" l="2/3" m="5/6" s="1/1" className="u-block-center">
          <IconList className="u-ml-xlarge@s-up">
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip6')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip7')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip8')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:videoTips:tip9')}</HtmlContent>
            </IconList.Item>
          </IconList>
        </Layout.Item>
      </ContentSection>
      <ContentSection className="u-text-center">
        <Heading level={3}>{t('landingPage:heading:photoTipsId')}</Heading>
        <HtmlContent as={'h3'}>{t('landingPage:content:photoTipsId:text1')}</HtmlContent>
        <ContentSection>
          <Layout className="o-layout o-layout--block-center o-layout--inline-center">
            <Layout.Item default="1/4" m="1/3" s="1/1">
              <img src={`${process.env.PUBLIC_URL}/assets/landingPage/License1-new.png`} />
            </Layout.Item>
            <Layout.Item default="1/4" m="1/3" s="1/1">
              <img src={`${process.env.PUBLIC_URL}/assets/landingPage/passport1-new.png`} alt="Passport-Valid" />
            </Layout.Item>
          </Layout>
        </ContentSection>
        <Layout.Item default="1/2" l="2/3" m="5/6" s="1/1" className="u-block-center">
          <IconList className="u-ml-xlarge@s-up">
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip1')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip2')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip3')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip4')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip5')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip6')}</HtmlContent>
            </IconList.Item>
          </IconList>
        </Layout.Item>
      </ContentSection>

      <ContentSection>
        <HtmlContent as={'h3'} className="u-mt u-text-center">
          {t('landingPage:content:photoTipsId:text2')}
        </HtmlContent>
        <Layout center>
          <Layout.Item default="1/4" m="1/3" s="1/1" className="u-text-center">
            <img src={`${process.env.PUBLIC_URL}/assets/landingPage/group.png`} alt="Passport-Valid" />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1" className="u-text-center">
            <img src={`${process.env.PUBLIC_URL}/assets/landingPage/invalid-passport2.png`} alt="Passport-Invalid1" />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1" className="u-text-center">
            <img src={`${process.env.PUBLIC_URL}/assets/landingPage/invalid-passport3.png`} alt="Passport-Invalid2" />
          </Layout.Item>
        </Layout>
      </ContentSection>
      <ContentSection>
        <Layout.Item default="1/2" l="2/3" m="5/6" s="1/1" className="u-block-center">
          <IconList>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip7')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip8')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip9')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip10')}</HtmlContent>
            </IconList.Item>
            <IconList.Item icon="semantic-arrow-right">
              <HtmlContent>{t('landingPage:content:photoTipsId:tip11')}</HtmlContent>
            </IconList.Item>
          </IconList>
        </Layout.Item>
      </ContentSection>
      <ContentSection>
        <HtmlContent as={Paragraph} className="u-text-center ">
          {t('landingPage:endText')}
        </HtmlContent>
        <ButtonContainer center>
          <Button onClick={onLegitimationStartClick}>{t('landingPage:buttons:continue')}</Button>
        </ButtonContainer>
      </ContentSection>
      {showEnableOnfidoBtn && (
        <ButtonContainer center>
          <Button onClick={() => setIsOnfidoMotionEnabled(!isOnfidoMotionEnabled)}>
            {isOnfidoMotionEnabled ? t('landingPage:onfidoButton:disable') : t('landingPage:onfidoButton:enable')}
          </Button>
        </ButtonContainer>
      )}
      <BackToTop>{t('landingPage:buttons:backToTop')}</BackToTop>
    </>
  );
};

export default LegitimationLandingPage;
